import { useCallback, useState } from 'react';
import { Table, Modal, Form, Switch, Collapse, Alert, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  addWispoPushNotifications,
  NotificationSetting,
  NotificationType,
  PATIENT_NOTIFICATION_TYPES,
  EMPLOYEE_NOTIFICATION_TYPES,
  useUpdateNotificationsMutation,
} from '@app/notifications/api';
import { useUserContextSelector } from '@app/main/utils';
import { notify } from '@app/ui';
import { useNotificationSettingsColumns } from './useNotificationsSettingsColumns';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ROLE_VALUES_FOR_EMPLOYEES } from '@app/main/api';
import { features } from '@app/base';

function useUpdate(id: string) {
  const [update] = useUpdateNotificationsMutation();

  return useCallback(
    async (setting: NotificationSetting[]) => {
      update({ userId: id, setting })
        .unwrap()
        .then(() => notify.success.t('notifications.settings.updated'));
    },

    [update, id],
  );
}

interface Props {
  onClose: () => void;
}

function useDefaultNotificationSettings() {
  const [settings, isPatient, isEmployee] = useUserContextSelector((x) => [
    x.notificationSettings || [],
    x.hasRole('PATIENT'),
    ROLE_VALUES_FOR_EMPLOYEES.some((role) => x.hasRole(role)),
  ]);
  const result = [...settings];

  if (isPatient) {
    const missingSettings = PATIENT_NOTIFICATION_TYPES.filter(
      (type) => !result.some((s) => s.type === type),
    ).map((type) => ({ type: type as NotificationType, internal: true, email: false }));

    result.push(...missingSettings);
  }

  if (isEmployee) {
    const missingSettings = EMPLOYEE_NOTIFICATION_TYPES.filter(
      (type) => !result.some((s) => s.type === type),
    ).map((type) => ({ type: type as NotificationType, internal: true, email: false }));

    result.push(...missingSettings);
  }

  return result;
}

export function NotificationsSettingsPanel(props: Props) {
  const { onClose } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'notifications.settings' });
  const id = useUserContextSelector((x) => x.id);
  const update = useUpdate(id);
  const [form] = Form.useForm();

  const dataSource = useDefaultNotificationSettings().map((setting) => ({
    type: setting.type,
    internal: setting ? setting.internal : true,
    email: setting ? setting.email : false,
  }));

  const [selectAll, setSelectAll] = useState({
    email: dataSource.every((x) => x.email === true),
    internal: dataSource.every((x) => x.internal === true),
  });
  const handleSelectAll = (column: 'email' | 'internal') => (e: CheckboxChangeEvent) => {
    const newValue = e.target.checked;
    setSelectAll((prev) => ({ ...prev, [column]: newValue }));

    dataSource.forEach((setting) => {
      form.setFieldsValue({
        [`${column}_${setting.type}`]: newValue,
      });
    });
  };

  const columns = useNotificationSettingsColumns(selectAll, handleSelectAll);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const result = dataSource.map((setting) => ({
        type: setting.type,
        internal:
          values[`internal_${setting.type}`] !== undefined
            ? values[`internal_${setting.type}`]
            : setting.internal,
        email:
          values[`email_${setting.type}`] !== undefined ? values[`email_${setting.type}`] : setting.email,
      }));
      update(result);
    });
  };

  return (
    <Modal width="70%" open title={t('title')} onCancel={onClose} onOk={handleOk}>
      {features.isEnabled('WebPushNotifications') && (
        <Collapse>
          <WebPushNotifications />
        </Collapse>
      )}
      <Form form={form}>
        <Table columns={columns} dataSource={dataSource} rowKey="type" />
      </Form>
    </Modal>
  );
}

// AntD is using props to pass data to the Collapse.Panel
const WebPushNotifications = (props: object) => {
  const { t: tp } = useTranslation(undefined, { keyPrefix: 'notifications.settings.webPushNotifications' });
  const [loading, setLoading] = useState(false);

  if (!('serviceWorker' in navigator)) {
    return null;
  }

  return (
    <Collapse.Panel
      key="webPushNotifications"
      {...props}
      header={
        <Space direction="horizontal">
          <Switch
            loading={loading}
            onChange={async (_, event) => {
              event.stopPropagation();
              setLoading(true);
              try {
                const result = await Notification.requestPermission();
                if (result !== 'granted') return;

                await addWispoPushNotifications();
              } finally {
                setLoading(false);
              }
            }}
            disabled={Notification.permission !== 'default'}
            checked={Notification.permission === 'granted'}
          />
          <Typography.Text>{tp('name')}</Typography.Text>
        </Space>
      }
    >
      <Space direction="vertical" className="w-100">
        <Alert type="info" message={tp('descriptionAlert')} />
        {Notification.permission === 'denied' && <Alert type="warning" message={tp('deniedAlert')} />}
        {Notification.permission === 'granted' && <Alert type="warning" message={tp('disableAlert')} />}
      </Space>
    </Collapse.Panel>
  );
};
